import { createContext, use, type Context } from 'react';

/**
 * A context the app always provides, and the hook that reads it. Reading it outside its
 * provider is a wiring bug, so the hook throws instead of returning a default.
 *
 * ```ts
 * export const [BrandContext, useBrand] = createRequiredContext<BrandConfig>('BrandContext');
 * ```
 */
export function createRequiredContext<T>(name: string): readonly [Context<T | null>, () => T] {
  const RequiredContext = createContext<T | null>(null);
  RequiredContext.displayName = name;

  function useRequiredContext(): T {
    const value = use(RequiredContext);
    if (value === null) {
      throw new Error(`${name} is missing: render this inside its provider`);
    }
    return value;
  }

  return [RequiredContext, useRequiredContext];
}
